"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveEntity = exports.isFetcherConflictError = exports.isAxiosError = exports.isConflictError = exports.getApplicationErrorFromFetcherError = exports.getErrorDtoFromFetcherError = exports.isFetcherErrorLike = exports.handleResponseValidation = exports.isSuccessResponse = exports.getUrl = exports.getUrlWithQuery = exports.fetchData = exports.getClassValidatorResponseHandlerFn = exports.getHttpsAgent = exports.axiosStatusValidator = exports.getDefaultAxiosFetcher = void 0;
const https_1 = __importDefault(require("https"));
const querystring_1 = __importDefault(require("querystring"));
const core_1 = require("@newfront-insurance/core");
const errors_1 = require("@newfront-insurance/errors");
const axios_1 = __importDefault(require("axios"));
const http_status_codes_1 = require("http-status-codes");
const fetcher_error_1 = require("./fetcher-error");
function getDefaultAxiosFetcher() {
    return axios_1.default;
}
exports.getDefaultAxiosFetcher = getDefaultAxiosFetcher;
/**
 * Never throw for any valid http errors.
 * Default axois behavior is to Promise.reject for any non-2xx-3xx status code.
 * This causes axios to only throw for non-http-related errors
 */
function axiosStatusValidator(status) {
    return status <= 599;
}
exports.axiosStatusValidator = axiosStatusValidator;
function getHttpsAgent(disableStrictSsl = false) {
    return disableStrictSsl
        ? new https_1.default.Agent({
            rejectUnauthorized: false,
        })
        : undefined;
}
exports.getHttpsAgent = getHttpsAgent;
/**
 * Helper function for class validator repsonses
 * The expected response is the converted data as well as any validation errors
 *
 * ```typescript
 * import { ClassType, validator, plainToClass } from 'class-validator';
 * import { Validator, getClassValidatorRepsonseHandlerFn } from '@newfront-insurance/fetcher';
 *
 * function getResponseHandler<T>(validatorClass: ClassType<T>): Validator<T> {
 *   return getClassValidatorResponseHandlerFn<T>)(async (data) => {
 *     const value = plainToClass(validatorClass, data);
 *     return { errors: await validator(value), value}
 *   })
 * }
 * ```
 */
function getClassValidatorResponseHandlerFn(validate) {
    return async (responseData) => {
        const { value, errors } = await validate(responseData);
        const message = errors
            .map((e) => e.toString())
            .join('\n')
            .trim();
        return {
            error: message.length > 0 ? new Error(message) : undefined,
            value,
        };
    };
}
exports.getClassValidatorResponseHandlerFn = getClassValidatorResponseHandlerFn;
async function fetchData(axiosFetcher, config, authProfileHandler, maxAuthRetry, logger) {
    let { requestAuthHeaders, fetcherResponse } = await getFetchedResponse(axiosFetcher, config, {}, await authProfileHandler(), logger);
    let attempt = 0;
    /* eslint-disable no-await-in-loop */
    while (isAuthFailureResponse(fetcherResponse) && attempt < maxAuthRetry) {
        const authProfileResult = await authProfileHandler();
        if (!shouldAuthRetry(authProfileResult)) {
            break;
        }
        const { requestAuthHeaders: newRequestAuthHeaders, fetcherResponse: newFetcherResponse } = await getFetchedResponse(axiosFetcher, config, requestAuthHeaders, authProfileResult, logger);
        requestAuthHeaders = newRequestAuthHeaders;
        fetcherResponse = newFetcherResponse;
        attempt += 1;
    }
    /* eslint-enable no-await-in-loop */
    return fetcherResponse;
}
exports.fetchData = fetchData;
async function getFetchedResponse(axiosFetcher, config, previousAuthHeaders, authProfileResult, logger) {
    const newAuthHeaders = getAuthProfileHeaders(authProfileResult);
    const updatedConfig = {
        ...config,
        headers: withReplacedAuthHeaders(config.headers || {}, previousAuthHeaders, newAuthHeaders),
    };
    logger.info(`Fetching [${config.method}]: ${getUrl(config)}`, {
        method: updatedConfig.method,
        url: getUrlWithQuery(updatedConfig),
        headerKeys: Object.keys(updatedConfig.headers || {}),
    });
    const response = await axiosFetcher(updatedConfig);
    return {
        requestAuthHeaders: newAuthHeaders,
        fetcherResponse: {
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        },
    };
}
function getAuthProfileHeaders(result) {
    return shouldAuthRetry(result) && result.authProfile ? result.authProfile.getAuthHeaders() : {};
}
/**
 * Returns the headers with the previous auth headers replaced with the new ones
 */
function withReplacedAuthHeaders(headers, previousAuthHeaders, nextAuthHeaders) {
    return withAuthHeaders(clearAuthHeaders(headers, Object.keys(previousAuthHeaders)), nextAuthHeaders);
}
/**
 * Returns the headers with the auth headers inserted
 */
function withAuthHeaders(headers, authHeaders) {
    return {
        ...clearAuthHeaders(headers, Object.keys(authHeaders)),
        ...authHeaders,
    };
}
function clearAuthHeaders(headers, authHeaderKeys) {
    const updatedHeaders = { ...headers };
    authHeaderKeys.forEach((key) => delete updatedHeaders[key]);
    return updatedHeaders;
}
/**
 * Returns the full url with query parameters
 */
function getUrlWithQuery(config) {
    let fullUrl = getUrl(config);
    if (config.params && Object.keys(config.params).length > 0) {
        fullUrl += `?${querystring_1.default.stringify(config.params)}`;
    }
    return fullUrl;
}
exports.getUrlWithQuery = getUrlWithQuery;
/**
 * Returns the full url without query parameters
 */
function getUrl(config) {
    // Disabling the nullish coalescing as an empty string is not nullish
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return buildBaseUrl(config.baseURL || '', config.url || '');
}
exports.getUrl = getUrl;
function buildBaseUrl(baseUrl, endpoint) {
    return `${(0, core_1.trimEnd)(baseUrl, '/')}/${(0, core_1.trimStart)(endpoint, '/')}`;
}
function shouldAuthRetry(authResult) {
    return (0, core_1.isNil)(authResult.shouldRetry) || authResult.shouldRetry;
}
function isAuthFailureResponse(response) {
    return response.statusCode === http_status_codes_1.StatusCodes.UNAUTHORIZED;
}
function isSuccessResponse(response) {
    return response.statusCode >= http_status_codes_1.StatusCodes.OK && response.statusCode < http_status_codes_1.StatusCodes.BAD_REQUEST;
}
exports.isSuccessResponse = isSuccessResponse;
async function handleResponseValidation(response, validator, url) {
    const validationResult = await validator(response.data);
    if (hasValidationError(validationResult.error)) {
        throw new fetcher_error_1.FetcherError(`Validation Error in ${url} Response: ${getValidationResultMessages(validationResult.error)}`, response, validationResult);
    }
    // todo(nickzelei): add log for validation warnings
    return { data: validationResult.value };
}
exports.handleResponseValidation = handleResponseValidation;
function hasValidationError(error) {
    return !(0, core_1.isNil)(error) && (0, core_1.toArray)(error).length > 0;
}
function getValidationResultMessages(error) {
    return (0, core_1.toArray)(error)
        .map((e) => e.message)
        .join('\n');
}
/**
 * Checks if the error is a FetcherError or looks like one
 */
function isFetcherErrorLike(error) {
    return (((0, core_1.isFunction)(error) && error.name === 'FetcherError') ||
        error instanceof fetcher_error_1.FetcherError ||
        ((0, core_1.isObject)(error) && !(0, core_1.isNil)(error.fetcherResponse)));
}
exports.isFetcherErrorLike = isFetcherErrorLike;
/**
 * Attempts to extract an ErrorDto from the FetcherError.
 */
function getErrorDtoFromFetcherError(error) {
    const maybeErrorDto = error.fetcherResponse.data;
    return (0, errors_1.isErrorDtoLike)(maybeErrorDto) ? maybeErrorDto : null;
}
exports.getErrorDtoFromFetcherError = getErrorDtoFromFetcherError;
/**
 * Attempts to extract an ErrorDto from the FetcherError and convert it to a generic ApplicationError.
 */
function getApplicationErrorFromFetcherError(error) {
    const errorDto = getErrorDtoFromFetcherError(error);
    return errorDto
        ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            new errors_1.ApplicationError(errorDto.message || 'No message provided', errorDto.errorCode, errorDto.category)
        : null;
}
exports.getApplicationErrorFromFetcherError = getApplicationErrorFromFetcherError;
/**
 * Checks if the error is:
 *  An ApplicationErrorLike with category ALREADY_EXISTS
 *  An AxiosError with HTTP code 409 (CONFLICT)
 *  An AxiosError with ErrorDtoLike body with category ALREADY_EXISTS
 */
function isConflictError(error) {
    return isFetcherConflictError(error) || isApplicationConflictError(error) || isAxiosConflictError(error);
}
exports.isConflictError = isConflictError;
function isApplicationConflictError(error) {
    return (0, errors_1.isApplicationErrorLike)(error) && error.category === errors_1.ErrorTypeCategory.ALREADY_EXISTS;
}
const CONFLICT_STR = http_status_codes_1.StatusCodes.CONFLICT.toString();
function isAxiosConflictError(error) {
    var _a;
    if (isAxiosError(error)) {
        if (error.code === CONFLICT_STR) {
            return true;
        }
        const data = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
        return (0, errors_1.isErrorDtoLike)(data) && data.category === errors_1.ErrorTypeCategory.ALREADY_EXISTS;
    }
    return false;
}
/**
 * Checks to see if the input is an AxiosError
 */
function isAxiosError(error) {
    return !(0, core_1.isNil)(error) && (0, core_1.isObject)(error) && error.isAxiosError === true;
}
exports.isAxiosError = isAxiosError;
/**
 * Checks to see if the input is an FetcherError with statusCode 409
 */
function isFetcherConflictError(error) {
    if (isFetcherErrorLike(error)) {
        const applError = getApplicationErrorFromFetcherError(error);
        return (applError && isApplicationConflictError(applError)) || false;
    }
    return false;
}
exports.isFetcherConflictError = isFetcherConflictError;
/**
 * Attempts to create an entity. Checks if a conflict error is returned and then attempts to retrieve it.
 */
async function resolveEntity(create, retrieve, logger) {
    try {
        return await create();
    }
    catch (createError) {
        if (!isConflictError(createError)) {
            throw createError;
        }
        logger.info('Found conflict, attempting to retrieve');
        try {
            return await retrieve();
        }
        catch (retrieveError) {
            // Log the retrieve error and throw the original create error
            logger.error(retrieveError);
            throw createError;
        }
    }
}
exports.resolveEntity = resolveEntity;
//# sourceMappingURL=util.js.map